import React from 'react';
import { Transition } from 'react-transition-group';
import { TweenMax, Back } from 'gsap';
import WorkbooksMenuItem from './WorkbooksMenuItem';
import { getLastPathFor } from '../../util/local-storage-util';
import { Workbook, License, Product } from 'types';
import map from 'lodash/map';
import filter from 'lodash/filter';
import { getLocale } from '../../translations/translator';

import {
  getGradeLevelDescription,
  getClickAwayResourcesLink,
  getWorkbookLink
} from '../../util/workbook-util';
import { getBadgeTypeForWorkbook } from 'state/modules/user/util';

type Props = {
  workbooks: Array<Workbook>;
  show: boolean;
  userId: number | null;
  onWorkbookClick?: (isOpen?: boolean) => void;
  itemStyles?: any;
  licenses: Array<License>;
  clickAways?: any[]; // TODO type
  allWorkbooksWithSameLicenseType?: boolean;
};

const getClickAway = (clickAways, product: Product) => {
  return clickAways?.find(ca => ca.productCurriculumId === product.productCurriculumId);
};

const getRouteForBook = (workbook: Workbook, clickAways, userId): string => {
  // if the user only has access to "A Click Away", send them there regardless

  //TODO: License Expiration issue
  const isClickAway = getClickAway(clickAways, workbook.products[0]);
  if (isClickAway /*&& !license.expired*/) {
    return getClickAwayResourcesLink(workbook.edition as string, workbook.bookCode);
  }

  const bookParams = {
    bookCode: workbook.bookCode,
    edition: workbook.edition
  };
  let path;
  if (userId) {
    path = getLastPathFor(userId, { ...bookParams });
  }

  return path || getWorkbookLink(bookParams);
};

const WorkbookMenu: React.FC<Props> = ({
  workbooks,
  show,
  onWorkbookClick,
  userId,
  itemStyles,
  licenses,
  clickAways = [],
  allWorkbooksWithSameLicenseType
}) => {
  const animate: any = (list: HTMLUListElement): TweenMax => {
    TweenMax.staggerFromTo(
      list.children,
      0.3,
      {
        scale: show ? 0 : 1,
        opacity: show ? 0 : 1
      },
      {
        delay: show ? 0.5 : 0,
        scale: show ? 1 : 0,
        opacity: show ? 1 : 0,
        ease: Back.easeOut.config(2.5)
      },
      show ? 0.35 : 0
    );
  };

  const getStyles: any = (
    wb: Workbook,
    licenses: Array<License>,
    itemStyles?: any,
    allWorkbooksWithSameLicenseType?: boolean
  ): any => {
    let workbookItemSize: any | Array<any>;

    if (itemStyles) {
      if (allWorkbooksWithSameLicenseType) {
        workbookItemSize = itemStyles.workbookItemLarge;
      } else if (getBadgeTypeForWorkbook(licenses, clickAways, wb)) {
        workbookItemSize = [itemStyles.workbookItemLarge, itemStyles.workbookItemSmall];
      } else {
        workbookItemSize = [itemStyles.workbookItemLarge, itemStyles.workbookItemMedium];
      }

      return {
        workbookTitle: itemStyles.workbookTitle,
        gradeLevelDescription: itemStyles.gradeLevelDescription,
        workbookItem: workbookItemSize
      };
    }

    return {};
  };

  const filteredWorkbooks = filter(
    workbooks,
    wb => wb.type === 'workbook' && wb.locale === getLocale()
  );

  return (
    <Transition
      in={show}
      timeout={500}
      onEnter={animate}
      onExit={animate}
      mountOnEnter={true}
      unmountOnExit={true}
      appear={true}
    >
      <ul
        style={{
          display: 'flex',
          flexFlow: 'row wrap',
          overflow: 'hidden',
          listStyle: 'none',
          padding: '0 14px 5px 10px'
        }}
      >
        {map(filteredWorkbooks, (wb: Workbook) => (
          <WorkbooksMenuItem
            workbook={wb}
            onWorkbookClick={onWorkbookClick}
            key={wb.id}
            routeTo={getRouteForBook(wb, clickAways, userId)}
            styles={getStyles(wb, licenses, itemStyles, allWorkbooksWithSameLicenseType)}
            renderWorkbookBadge={!!getBadgeTypeForWorkbook(licenses, clickAways, wb)}
            badgeType={getBadgeTypeForWorkbook(licenses, clickAways, wb)}
            gradeLevelDescription={getGradeLevelDescription(wb.gradeLevel)}
          />
        ))}
      </ul>
    </Transition>
  );
};

export default WorkbookMenu;
